import { useLocation } from "@reach/router"
import { useStaticQuery, graphql } from "gatsby"
import { SbGatsbyStory, storyblokEditable } from "gatsby-source-storyblok"
import React, {
  useLayoutEffect,
  lazy,
  useEffect,
  startTransition,
  useState,
  Suspense,
} from "react"

import ReminderBanner from "./experiments/ReminderBanner"
import { useModalControl } from "./StoryBlok/V2/Section/ModalSection"
import ToastNotification from "./V2/Banners/ToastNotification"
import WebToLeadForm from "./V2/Forms/WebToLeadForm"
import Navigation from "./V2/Navigation"
import { GetStartedModal } from "./V2/Sections/GetStarted/Modal"
import Modal from "./V2/Sections/Modal"
import "aos/dist/aos.css"
import "./layout.css"
import { useSignupReminderStore } from "../cart/reminder"
import NavigationExperiment from "../experiments/SaveAStep"
import { mapContentHighlight } from "../utils/V2/contentHighlight"
import { mapTopNavigationMainLinks } from "../utils/V2/storyblok"

import { Experiment } from "@constants/experiments"
import { Color } from "@constants/V2/color"
import { useIsExperiment } from "@hooks/experiments"
import { getUrlFromStoryblokLink, linkIsNotNull } from "@utils/storyblok"
import { storeQueryString } from "@utils/url"
import { useIsBreakpoint } from "@utils/V2/screen"

interface Props {
  children: any
}
interface QueryResult {
  footer: Storyblok.Page & SbGatsbyStory
  socialMedia: Storyblok.Page & SbGatsbyStory
  siteWideBanner: Storyblok.Page & SbGatsbyStory
  siteWideBannerB: Storyblok.Page & SbGatsbyStory
}

type ToastBannerProps = {
  content: Array<
    | Storyblok.ToastNotificationIconBanner
    | Storyblok.ToastNotificationCountDownBanner
  >
}

/**
 * Directories that should not have a site wide banner
 */
const EXCLUDED_SITEWIDEBANNER_PATH_PREFIXES = [
  "/za/internal/tests/",
  "/za/shop/options/",
]

export const ToastBanner = ({ content }: ToastBannerProps) => {
  return content.map((toastItem, index) => {
    if (toastItem.component === "toastNotificationCountDownBanner") {
      const item = toastItem as Storyblok.ToastNotificationCountDownBanner

      return (
        <ToastNotification
          // @ts-expect-error to have more strict typing compared to Storybloks generic object typing
          {...storyblokEditable(toastItem)}
          key={index}
          text={item.text}
          ctaText={item.ctaText}
          ctaColor={item.ctaColor}
          textColor={item.textColor}
          component={item.component}
          timerLabel={item.timerLabel}
          ctaTextColor={item.ctaTextColor}
          timerTextColor={item.timerTextColor}
          timerTargetDate={item.timerTargetDate}
          backgroundColor={item.backgroundColor}
          ctaLink={getUrlFromStoryblokLink(item.ctaLink)}
          isSiteWideBanner={item.isSiteWideBanner}
        />
      )
    } else {
      const item = toastItem as Storyblok.ToastNotificationIconBanner
      return (
        <ToastNotification
          // @ts-expect-error to have more strict typing compared to Storybloks generic object typing
          {...storyblokEditable(toastItem)}
          key={index}
          text={item.text}
          mobileText={item.mobileText}
          ctaText={item.ctaText}
          iconAlt={item.icon?.alt}
          ctaColor={item.ctaColor}
          icon={item.icon?.filename}
          textColor={item.textColor}
          component={item.component}
          backgroundColor={item.backgroundColor}
          ctaLink={getUrlFromStoryblokLink(item.ctaLink)}
          isSiteWideBanner={item.isSiteWideBanner}
        />
      )
    }
  })
}

/**
 * @todo we cant set this type in `Storyblok.SiteWideBanner` directly
 * because namespaces dont let us import the SbGatsbyStory type
 */
type SiteWideBannerWithExcludedPages = Storyblok.SiteWideBanner & {
  excludedPages: (SbGatsbyStory & Storyblok.Page)[]
}

const useIsReminderBannerVisible = () =>
  useSignupReminderStore((state) => state.isReminderBannerVisible)

const Layout = ({ children }: Props) => {
  // const [isBrowserSupported, setIsBrowserSupported] = useState<boolean>(false)
  const location = useLocation()
  const Footer = lazy(() => import("./Footer"))
  const isReminderBannerVisible = useIsReminderBannerVisible()
  const reminderBannerExperimentEnabled = useIsExperiment(
    Experiment.REMINDER_BANNER
  )
  const updatedSitewideBannerExperimentEnabled = useIsExperiment(
    Experiment.UPDATED_SITEWIDE_BANNER
  )
  const removeSitewideBannerExperimentEnabled = useIsExperiment(
    Experiment.REMOVE_SITEWIDE_BANNER
  )

  const saveAStepNavExperimentEnabled = useIsExperiment(Experiment.SAVE_A_STEP)

  const refinedNavExperimentEnabled = useIsExperiment(
    Experiment.CONTENT_HIGHLIGHT_CTA
  )

  const [showFooter, setShowFooter] = useState(false)

  useEffect(() => {
    // Delay rendering the footer to ensure the children have loaded
    startTransition(() => {
      setShowFooter(true)
    })
  }, [])

  const queryResult: QueryResult = useStaticQuery(graphql`
    query StaticData {
      footer {
        uuid
        full_slug
        content
      }

      socialMedia {
        uuid
        full_slug
        content
      }

      siteWideBanner {
        uuid
        full_slug
        content
      }

      siteWideBannerB {
        uuid
        full_slug
        content
      }
    }
  `)

  const toastNotificationContent: (
    | Storyblok.ToastNotificationIconBanner
    | Storyblok.ToastNotificationCountDownBanner
  )[] = []

  /*
    A site wide banner can be set from config
  */
  let siteWideBannerData: SiteWideBannerWithExcludedPages

  if (updatedSitewideBannerExperimentEnabled) {
    siteWideBannerData = queryResult?.siteWideBannerB
      ?.content as SiteWideBannerWithExcludedPages
  } else {
    // Use the default banner
    siteWideBannerData = queryResult?.siteWideBanner
      ?.content as SiteWideBannerWithExcludedPages
  }

  // ToDo find out why this doesnt come back parsed from graphql query
  if (typeof siteWideBannerData === "string") {
    siteWideBannerData = JSON.parse(siteWideBannerData)
  }

  const excludedSiteWideBannerPages = siteWideBannerData?.excludedPages?.map(
    (page) => `/${page.full_slug}/`
  )

  /**
   * @param currentPath Always expects a path with a leading and trailing slash.
   * Example: `/za/internal/tests/`
   */
  const hideSitewideBanner = (currentPath: string): boolean => {
    return (
      EXCLUDED_SITEWIDEBANNER_PATH_PREFIXES.some((excluded_path) =>
        currentPath.startsWith(excluded_path)
      ) ||
      excludedSiteWideBannerPages.some(
        (excluded_page) => currentPath === excluded_page
      )
    )
  }

  /**
   * Extract the path parameter from location.search
   * @param searchString The location.search string
   * @returns The path value or empty string if not found
   */
  const getPathFromSearch = (searchString: string): string => {
    const urlParams = new URLSearchParams(searchString)
    const path = urlParams.get("path")
    if (!path) return ""

    // Remove any leading or trailing slashes and ensure exactly one of each
    const cleanPath = path.replace(/^\/+|\/+$/g, "")
    return `/${cleanPath}/`
  }

  if (
    // ToDo clean up these two location checks into one
    !hideSitewideBanner(location.pathname) &&
    !hideSitewideBanner(getPathFromSearch(location.search)) &&
    siteWideBannerData?.content?.length &&
    siteWideBannerData?.isEnabled === true
  ) {
    // Global banners always live at the top
    siteWideBannerData.content.forEach((content) => {
      content.isSiteWideBanner = true
    })

    toastNotificationContent.push(...siteWideBannerData.content)
  }

  /* NOTE: A page can return navigation as a part of its story content or
     a page query can include a query of the default site header. The following
     logic prioritises custom navigation set from a page's story, then checks to
     see if a query of the default header was added, and finally sets headerData
     to undefined if no header information can be found. The header will not
     render in this case.
   */
  const story = children?.[children.length - 1]?.props?.data?.story

  let pageData = story?.content as Storyblok.PageContent
  if (typeof pageData === "string") {
    pageData = JSON.parse(pageData)
  }

  if (pageData?.toastNotificationContent?.length) {
    // Local, page-specific banners, live below any global banners
    toastNotificationContent.push(...(pageData?.toastNotificationContent ?? []))
  }
  // This is to cater for the 404 page query
  const mode = pageData?.navigationMode ? pageData.navigationMode : "dark"

  const headerContent: { content: Storyblok.TopNavigation } | undefined =
    pageData
      ? pageData.navigation
      : // This is to cater for the 404 page query
        story?.navigation?.content
        ? { content: JSON.parse(story.navigation.content) }
        : undefined

  storeQueryString(location.search)

  useLayoutEffect(() => {
    import("aos").then(({ default: AOS }) => {
      AOS.init({ once: true, offset: 120, duration: 500, easing: "ease" })
    })
  }, [])

  const {
    isOpen: isOpenSalesModal,
    setIsOpen: setIsOpenSalesModal,
    modalParams,
  } = useModalControl("sales-modal")

  const { isOpen: isOpenSuccessModal, setIsOpen: setIsOpenSuccessModal } =
    useModalControl("contact-success")

  const isMobile = useIsBreakpoint("mobile")
  const isTablet = useIsBreakpoint("tablet")
  const hideOnCartStylingExperiment =
    location.pathname.includes("za/cart-overview") && (isMobile || isTablet)

  /*
    Hide the navigation hamburger menu if COUNTER EXPERIMENT PAGE + coming from an Advertisment affiliate link
   * */
  const urlParams = new URLSearchParams(location.search)
  const isAffiliateLink =
    urlParams.get("utm_medium") === "paidsearch" ||
    urlParams.get("utm_medium") === "paidsocial" ||
    urlParams.get("utm_medium") === "display"
  const hideOnCounterAffilatePage =
    location.pathname.includes("za/point-of-sale/yoco-counter-b") &&
    isAffiliateLink

  return (
    <>
      {/* 
        For /editor route:
          This will only render the siteWideBanner when not on the /settings/site-wide-banner/* story.
          This is done to prevent the site-wide-banner from rendering twice and breaking the 
          editor ui when on this path (ie: `editor/?path=settings/site-wide-banner/za`).

          See editor.tsx for rendering implementation of banners for the /editor route.

        For NON-/editor route: 
          This will render both siteWideBanner and any page banner
      */}
      {!location.search.includes("settings/site-wide-banner") &&
      !hideOnCartStylingExperiment &&
      toastNotificationContent.length &&
      !isReminderBannerVisible &&
      !removeSitewideBannerExperimentEnabled ? (
        <ToastBanner content={toastNotificationContent} />
      ) : null}
      {reminderBannerExperimentEnabled &&
        !location.pathname.includes("cart-overview") && <ReminderBanner />}
      {!hideOnCartStylingExperiment ? (
        headerContent ? (
          <div id="navigation">
            {saveAStepNavExperimentEnabled || refinedNavExperimentEnabled ? (
              <NavigationExperiment
                contentHighlight={mapContentHighlight(
                  headerContent.content.contentHighlight
                )}
                mode={mode}
                hideMobileMenu={hideOnCounterAffilatePage}
                mainLinks={mapTopNavigationMainLinks(
                  (headerContent.content as Storyblok.TopNavigation).mainLinks
                )}
                primaryButtonText={headerContent.content.primaryCTAText}
                primaryButtonLink={
                  headerContent.content.primaryCTALink &&
                  linkIsNotNull(headerContent.content.primaryCTALink)
                    ? getUrlFromStoryblokLink(
                        headerContent.content.primaryCTALink
                      )
                    : undefined
                }
                secondaryButtonText={headerContent.content.secondaryCTAText}
                secondaryButtonLink={getUrlFromStoryblokLink(
                  headerContent.content.secondaryCTALink
                )}
                mobilePrimaryButtonText={
                  headerContent.content.mobilePrimaryCTAText
                }
                mobilePrimaryButtonLink={
                  linkIsNotNull(headerContent.content.mobilePrimaryCTALink)
                    ? getUrlFromStoryblokLink(
                        headerContent.content.mobilePrimaryCTALink
                      )
                    : undefined
                }
                isBannerContents={
                  toastNotificationContent?.length ? true : false
                }
                showCartWidget={pageData?.showCartWidget}
                isBackgroundTransparent={pageData.isNavigationTransparent}
                trackingEvent={headerContent.content.trackingEvent}
                trackingEventKey={headerContent.content.trackingEventKey}
                trackingEventValue={headerContent.content.trackingEventValue}
                secondaryButtonTrackingEvent={
                  headerContent.content.secondaryCTATrackingEvent
                }
                secondaryButtonTrackingEventKey={
                  headerContent.content.secondaryCTATrackingEventKey
                }
                secondaryButtonTrackingEventValue={
                  headerContent.content.secondaryCTATrackingEventValue
                }
              />
            ) : (
              <Navigation
                mode={mode}
                hideMobileMenu={hideOnCounterAffilatePage}
                mainLinks={mapTopNavigationMainLinks(
                  (headerContent.content as Storyblok.TopNavigation).mainLinks
                )}
                primaryButtonText={headerContent.content.primaryCTAText}
                primaryButtonLink={
                  headerContent.content.primaryCTALink &&
                  linkIsNotNull(headerContent.content.primaryCTALink)
                    ? getUrlFromStoryblokLink(
                        headerContent.content.primaryCTALink
                      )
                    : undefined
                }
                secondaryButtonText={headerContent.content.secondaryCTAText}
                secondaryButtonLink={getUrlFromStoryblokLink(
                  headerContent.content.secondaryCTALink
                )}
                mobilePrimaryButtonText={
                  headerContent.content.mobilePrimaryCTAText
                }
                mobilePrimaryButtonLink={
                  linkIsNotNull(headerContent.content.mobilePrimaryCTALink)
                    ? getUrlFromStoryblokLink(
                        headerContent.content.mobilePrimaryCTALink
                      )
                    : undefined
                }
                isBannerContents={
                  toastNotificationContent?.length ? true : false
                }
                showCartWidget={pageData?.showCartWidget}
                isBackgroundTransparent={pageData.isNavigationTransparent}
                trackingEvent={headerContent.content.trackingEvent}
                trackingEventKey={headerContent.content.trackingEventKey}
                trackingEventValue={headerContent.content.trackingEventValue}
                secondaryButtonTrackingEvent={
                  headerContent.content.secondaryCTATrackingEvent
                }
                secondaryButtonTrackingEventKey={
                  headerContent.content.secondaryCTATrackingEventKey
                }
                secondaryButtonTrackingEventValue={
                  headerContent.content.secondaryCTATrackingEventValue
                }
              />
            )}
          </div>
        ) : null
      ) : null}
      <div className="flex min-h-screen flex-col">
        <main>
          {children}
          <GetStartedModal />
          <Modal
            backgroundColor={Color.White}
            title="Let's connect"
            body="Keen to chat? Catch us live during business hours (8am-5pm on weekdays) or get fast answers after-hours from our Bot Assistant."
            buttonText="Chat on WhatsApp"
            buttonLink="https://wa.me/27790743720?text=Hello"
            buttonBorderColor={Color.Blue}
            isOpen={isOpenSalesModal}
            onClick={() => setIsOpenSalesModal(false)}
          >
            <WebToLeadForm
              inModalId="contact-success"
              preselectedProduct={modalParams || undefined}
            />
          </Modal>
          <Modal
            backgroundColor={Color.White}
            title="Thanks for getting in touch!"
            body="Our team will contact you within 24 hours to discuss how we can help you grow. Keen to chat sooner than that? Reach out on WhatsApp chat."
            buttonText="Chat on WhatsApp"
            buttonLink="https://wa.me/27790743720?text=Hello"
            buttonBorderColor={Color.Green}
            isOpen={isOpenSuccessModal}
            onClick={() => setIsOpenSuccessModal(false)}
          />
        </main>
        {!pageData?.hide_footer &&
        showFooter &&
        !hideOnCartStylingExperiment ? (
          <Suspense>
            <Footer
              footer={queryResult.footer}
              socialMedia={queryResult.socialMedia}
            />
          </Suspense>
        ) : null}
      </div>
    </>
  )
}

export default Layout
